import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from "@sentry/react";
import { User } from '@supabase/supabase-js';

interface InactivityConfig {
  timeoutMs: number;
  warningMs: number;
  storageKey: string;
  events: string[];
}

const DEFAULT_CONFIG: InactivityConfig = {
  timeoutMs: 25 * 60 * 1000,
  warningMs: 23 * 60 * 1000,
  storageKey: 'last_activity_timestamp',
  events: ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'focus', 'wheel']
};

export function useInactivityTimer(
  config: Partial<InactivityConfig> = {},
  user: User | null,
  signOut: () => Promise<{ error: any }>,
  onAutoLogout?: () => void
) {
  const timeoutMs = config.timeoutMs ?? DEFAULT_CONFIG.timeoutMs;
  const warningMs = config.warningMs ?? DEFAULT_CONFIG.warningMs;
  const storageKey = config.storageKey ?? DEFAULT_CONFIG.storageKey;

  const [isWarning, setIsWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  // Use refs for mutable values that shouldn't trigger re-renders
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef<NodeJS.Timeout>();
  const warningTimerRef = useRef<NodeJS.Timeout>();
  const countdownRef = useRef<NodeJS.Timeout>();
  const isActiveRef = useRef(true);
  const signOutRef = useRef(signOut);
  const userRef = useRef(user);
  const onAutoLogoutRef = useRef(onAutoLogout);

  // Keep refs current without triggering effects
  signOutRef.current = signOut;
  userRef.current = user;
  onAutoLogoutRef.current = onAutoLogout;

  const performAutoLogout = useCallback(async () => {
    if (!userRef.current) return;
    try {
      await signOutRef.current();
      onAutoLogoutRef.current?.();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'auth_error', component: 'useInactivityTimer', operation: 'auto_logout' },
        extra: { message: 'Auto logout failed', userId: userRef.current?.id }
      });
    }
  }, []);

  const clearAllTimers = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  const updateActivity = useCallback(() => {
    if (!userRef.current || !isActiveRef.current) return;

    const now = Date.now();
    lastActivityRef.current = now;
    setIsWarning(false);

    try {
      localStorage.setItem(storageKey, now.toString());
    } catch {
      // Ignore localStorage errors
    }

    clearAllTimers();

    warningTimerRef.current = setTimeout(() => {
      if (!isActiveRef.current) return;

      const totalCountdown = Math.ceil((timeoutMs - warningMs) / 1000);
      setIsWarning(true);
      setRemainingSeconds(totalCountdown);

      let seconds = totalCountdown;
      countdownRef.current = setInterval(() => {
        seconds--;
        if (seconds <= 0) {
          clearInterval(countdownRef.current);
          performAutoLogout();
        } else {
          setRemainingSeconds(seconds);
        }
      }, 1000);
    }, warningMs);

    timerRef.current = setTimeout(() => {
      if (isActiveRef.current) {
        performAutoLogout();
      }
    }, timeoutMs);
  }, [storageKey, timeoutMs, warningMs, clearAllTimers, performAutoLogout]);

  const resetTimer = useCallback(() => {
    setIsWarning(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    updateActivity();
  }, [updateActivity]);

  const checkCrossTabActivity = useCallback(() => {
    try {
      const storedActivity = localStorage.getItem(storageKey);
      if (storedActivity) {
        const lastActivity = parseInt(storedActivity, 10);
        const timeSinceActivity = Date.now() - lastActivity;

        if (lastActivity > lastActivityRef.current && timeSinceActivity < timeoutMs) {
          lastActivityRef.current = lastActivity;
          setIsWarning(false);
          if (countdownRef.current) clearInterval(countdownRef.current);
        }
      }
    } catch {
      // Ignore cross-tab check errors
    }
  }, [storageKey, timeoutMs]);

  // Setup activity listeners
  useEffect(() => {
    if (!user) {
      isActiveRef.current = false;
      return;
    }

    isActiveRef.current = true;

    const events = DEFAULT_CONFIG.events;
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey) {
        checkCrossTabActivity();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    updateActivity();

    const crossTabInterval = setInterval(checkCrossTabActivity, 10000);

    return () => {
      isActiveRef.current = false;
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      window.removeEventListener('storage', handleStorageChange);
      clearAllTimers();
      clearInterval(crossTabInterval);
    };
  }, [user, updateActivity, checkCrossTabActivity, storageKey, clearAllTimers]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && userRef.current) {
        const timeSinceActivity = Date.now() - lastActivityRef.current;
        if (timeSinceActivity >= timeoutMs) {
          performAutoLogout();
        } else {
          updateActivity();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [timeoutMs, performAutoLogout, updateActivity]);

  return {
    isWarning,
    remainingSeconds,
    resetTimer,
    isActive: !!user && isActiveRef.current
  };
}
